import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TableContext } from '../../contexts/TableContext';

const ROWS_OPTIONS = [10, 20, 50, 100];

/** numbered window with ellipses: 1 … p-1 p p+1 … last */
const pageWindow = (page: number, pageCount: number): (number | '…')[] => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const pages = Array.from(new Set([1, pageCount, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pageCount)));
  const sorted = pages.sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push('…');
    out.push(p);
  });
  return out;
};

const PagerRowsSelect: React.FC<{
  value: number;
  onChange?: (_: number) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={rootRef} className="position-relative" style={{ width: '75px' }}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          height: '32px',
          width: '75px',
          padding: '5px 10px',
          fontSize: '13px',
          fontFamily: 'var(--font-sans)',
          lineHeight: '1.2',
          borderRadius: 'var(--radius-ctl, 12px)',
          border: '1px solid var(--line2)',
          backgroundColor: 'var(--surf2)',
          color: 'var(--txt)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
      >
        <span>{value}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s ease',
            color: 'var(--faint)',
            flexShrink: 0,
          }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 50,
            backgroundColor: 'var(--surf3)',
            border: '1px solid var(--line2)',
            borderRadius: 'var(--radius-ctl, 12px)',
            boxShadow: 'var(--shadow)',
            padding: '4px',
          }}
        >
          {ROWS_OPTIONS.map((val) => (
            <button
              key={val}
              type="button"
              onClick={() => {
                onChange?.(val);
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '6px 8px',
                fontSize: '13.5px',
                fontFamily: 'var(--font-sans)',
                textAlign: 'center',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: 'transparent',
                color: val === value ? 'var(--acc-text)' : 'var(--txt)',
                fontWeight: val === value ? 600 : 400,
                cursor: 'pointer',
                transition: 'background-color 0.15s ease, color 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--surf)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              {val}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface PaginationProps {
  page?: number;
  total?: number;
  perPage?: number;
  onPageChange?: (_: number) => void;
  onPerPageChange?: (_: number) => void;
}

/** Folio-style pager matching CoHostZone (§6.5) — left count · center page nav · right rows per page */
const Pagination: React.FC<PaginationProps> = ({
  page: propPage,
  total: propTotal,
  perPage: propPerPage,
  onPageChange,
  onPerPageChange,
}) => {
  const { t } = useTranslation();
  const context = useContext(TableContext);

  const page = propPage ?? context?.page ?? 1;
  const dataLength = Array.isArray(context?.data) ? context.data.length : 0;
  const total = propTotal ?? (context?.total > 0 ? context.total : dataLength);
  const perPage = propPerPage ?? context?.perPage ?? 10;
  const handlePageChange = onPageChange ?? context?.setPage;
  const handlePerPageChange = onPerPageChange ?? context?.setPerPage;

  const pageCount = Math.max(Math.ceil(total / perPage), 1);
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = total === 0 ? 0 : Math.min(page * perPage, total);

  return (
    <div className="evc-pager">
      {/* LEFT — record count */}
      <span className="evc-pager-count">
        <span className="num">{from}</span>–<span className="num">{to}</span> {t('common.pagination.of') || 'OF'}{' '}
        <span className="num">{total.toLocaleString()}</span>
      </span>

      {/* CENTER — page navigation */}
      <div className="evc-pager-nav">
        <button
          type="button"
          className="evc-pager-btn"
          aria-label={t('common.pagination.previous')}
          disabled={page <= 1}
          onClick={() => handlePageChange && handlePageChange(page - 1)}
        >
          ←
        </button>
        {pageWindow(page, pageCount).map((p, i) =>
          p === '…' ? (
            <span key={`gap-${i}`} className="evc-pager-gap">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              aria-current={p === page ? 'page' : undefined}
              onClick={() => handlePageChange && handlePageChange(p)}
              className={`evc-pager-btn${p === page ? ' active' : ''}`}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          className="evc-pager-btn"
          aria-label={t('common.pagination.next')}
          disabled={page >= pageCount}
          onClick={() => handlePageChange && handlePageChange(page + 1)}
        >
          →
        </button>
      </div>

      {/* RIGHT — rows per page selector */}
      <div className="evc-pager-perpage d-flex align-items-center justify-content-end gap-2">
        <span className="evc-pager-label">
          {t('common.pagination.rowsPerPage') && !t('common.pagination.rowsPerPage').startsWith('common.')
            ? t('common.pagination.rowsPerPage')
            : 'Rows per page'}
        </span>
        <PagerRowsSelect value={perPage} onChange={handlePerPageChange} />
      </div>
    </div>
  );
};

export default Pagination;
